// Library — saved words list with seen-count dots, "I know this" button, and
// a separate Mastered section. The "+" in the header opens a free-input
// dialog: type/paste any hz, AI fills in pinyin + meaning, save.

import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Plus, Star, GraduationCap, X } from 'lucide-react';
import type { LibraryWord, Word } from '@/lib/types';
import { getAIClient } from '@/lib/ai/client';
import { Events, track } from '@/lib/observability';
import { ScreenBackdrop } from '@/components/visual/ScreenBackdrop';
import { SealStamp } from '@/components/visual/SealStamp';
import { InkMountains } from '@/components/visual/InkMountains';
import { InkDivider } from '@/components/visual/InkDivider';
import { C } from '@/styles/tokens';

interface LibraryScreenProps {
  library: LibraryWord[];
  mastered: LibraryWord[];
  onOpenFlashcards: () => void;
  onMarkKnown: (hz: string) => void;
  onSaveWord: (word: Word) => void;
}

export function LibraryScreen({
  library,
  mastered,
  onOpenFlashcards,
  onMarkKnown,
  onSaveWord,
}: LibraryScreenProps) {
  const [addOpen, setAddOpen] = useState(false);
  return (
    <section className="relative h-full flex flex-col overflow-hidden" style={{ background: C.cream }}>
      <ScreenBackdrop mark="库" position="tl" size={260} rotate={-8} />
      <div
        className="flex items-center gap-3 px-4 py-3 relative"
        style={{ borderBottom: `1px solid ${C.blush}` }}
      >
        <h1 className="ff-display text-2xl flex-1 flex items-baseline gap-2" style={{ color: C.ink }}>
          Library
          <span className="ff-zh-serif text-base opacity-50">· 词库</span>
        </h1>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="px-3 py-1.5 rounded-full ff-body text-[10px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1.5"
          aria-label="Add a word"
          style={{ background: C.vermillion, color: 'white' }}
        >
          <Plus size={12} aria-hidden strokeWidth={2.75} />
          Add word
          <span className="ff-zh-serif text-[11px] tracking-normal opacity-80">加词</span>
        </button>
      </div>

      <div className="px-4 py-3 grid grid-cols-2 gap-3 relative">
        <div className="relative p-3 rounded-2xl overflow-hidden" style={{ background: C.paper }}>
          <span className="frame-corner-mark" aria-hidden>收</span>
          <div className="flex items-baseline gap-1.5 relative">
            <span className="ff-zh-serif text-[13px] font-semibold" style={{ color: C.inkLight }}>
              收藏中
            </span>
            <span className="ff-body text-[10px] uppercase tracking-[0.2em]" style={{ color: C.inkSofter }}>
              Saving
            </span>
          </div>
          <div className="ff-display text-3xl relative" style={{ color: C.ink }}>
            {library.length}
          </div>
        </div>
        <div className="relative p-3 rounded-2xl overflow-hidden" style={{ background: C.blush }}>
          <span className="frame-corner-mark alt" aria-hidden>掌</span>
          <div className="flex items-baseline gap-1.5 relative">
            <span className="ff-zh-serif text-[13px] font-semibold" style={{ color: C.vermillionDeep }}>
              已掌握
            </span>
            <span className="ff-body text-[10px] uppercase tracking-[0.2em]" style={{ color: C.vermillionDeep, opacity: 0.7 }}>
              Mastered
            </span>
          </div>
          <div className="ff-display text-3xl relative" style={{ color: C.vermillionDeep }}>
            {mastered.length}
          </div>
        </div>
      </div>

      <div className="px-4 relative">
        <button
          type="button"
          onClick={onOpenFlashcards}
          className="relative w-full py-4 rounded-2xl ff-body font-semibold text-white inline-flex items-center justify-center gap-2 overflow-hidden shadow-paper"
          style={{ background: C.ink }}
        >
          <InkMountains
            height={48}
            className="absolute left-0 right-0 bottom-0"
            tint="#FFFFFF"
          />
          <SealStamp char="练" size={32} rotate={8} className="absolute top-2 right-3" />
          <span className="relative inline-flex items-center gap-2">
            <GraduationCap size={16} aria-hidden /> Practice flashcards
            <span className="ff-zh-serif opacity-70 ml-1">· 练习</span>
          </span>
        </button>
      </div>

      {(library.length > 0 || mastered.length > 0) && (
        <InkDivider glyph="◇" className="mx-6 mt-3" />
      )}

      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6 space-y-2 relative">
        {library.length === 0 && mastered.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-12">
            <div className="ff-zh-serif text-7xl mb-4" style={{ color: C.gold, opacity: 0.5 }} aria-hidden>
              库
            </div>
            <div className="ff-display italic text-2xl mb-1" style={{ color: C.ink }}>
              Library is empty
            </div>
            <div className="ff-zh-serif text-sm mb-2" style={{ color: C.inkLight }}>
              词库是空的
            </div>
            <div
              className="ff-body text-[12px] max-w-[260px] leading-relaxed"
              style={{ color: C.inkLight }}
            >
              Save words from chats or stories — tap the + chips on a persona&apos;s reply, and
              they&apos;ll show up here for review.
            </div>
          </div>
        )}
        {library.map((w, i) => (
          <div
            key={w.hz}
            className="relative flex items-center gap-3 p-3 pl-4 rounded-2xl anim-slide-up overflow-hidden shadow-paper"
            style={{
              background: C.cream,
              border: `1px solid ${C.mist}`,
              animationDelay: `${Math.min(i, 8) * 30}ms`,
            }}
          >
            <span
              aria-hidden
              className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r"
              style={{ background: C.vermillion }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="ff-zh-serif text-2xl font-bold" style={{ color: C.ink }}>
                  {w.hz}
                </span>
                <span className="ff-body text-xs opacity-70" style={{ color: C.ink }}>
                  {w.py}
                </span>
              </div>
              <div className="ff-body text-[12px]" style={{ color: C.inkLight }}>
                {w.en}
              </div>
              <div
                className="mt-1 flex items-center gap-1.5"
                aria-label={`seen ${w.seenCount} times`}
              >
                <span
                  className="ff-body italic text-[10px]"
                  style={{ color: C.inkSofter }}
                >
                  Seen {w.seenCount} {w.seenCount === 1 ? 'time' : 'times'}
                </span>
                <span className="flex gap-0.5" aria-hidden>
                  {Array.from({ length: Math.min(5, w.seenCount) }).map((_, j) => (
                    <span
                      key={j}
                      className="block w-1.5 h-1.5 rounded-full"
                      style={{ background: C.gold }}
                    />
                  ))}
                  {w.seenCount > 5 && (
                    <span
                      className="ff-body text-[9px] ml-0.5"
                      style={{ color: C.inkSofter }}
                    >
                      +{w.seenCount - 5}
                    </span>
                  )}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onMarkKnown(w.hz)}
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1 text-white flex-shrink-0"
              style={{ background: C.jade }}
              aria-label={`Mark ${w.hz} as mastered`}
            >
              <Star size={11} aria-hidden /> I know this
              <span className="ff-zh-serif opacity-80 ml-0.5">我会了</span>
            </button>
          </div>
        ))}

        {mastered.length > 0 && (
          <>
            <h2 className="ff-display text-xl mt-6 mb-2 flex items-baseline gap-2" style={{ color: C.ink }}>
              Mastered
              <span className="ff-zh-serif text-sm opacity-50">· 已掌握</span>
            </h2>
            <ul className="flex flex-wrap gap-1.5">
              {mastered.map((w) => (
                <li
                  key={w.hz}
                  className="px-2 py-0.5 rounded-full ff-zh-serif text-sm"
                  style={{ background: C.paper, color: C.inkLight }}
                >
                  {w.hz}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {addOpen && (
        <AddWordDialog
          existing={library}
          onClose={() => setAddOpen(false)}
          onSave={(word) => {
            const isNew = !library.some((l) => l.hz === word.hz);
            onSaveWord(word);
            if (isNew) {
              track(Events.WordFirstEncountered, { hz: word.hz, source: 'free_input' });
            }
            setAddOpen(false);
          }}
        />
      )}
    </section>
  );
}

function AddWordDialog({
  existing,
  onClose,
  onSave,
}: {
  existing: LibraryWord[];
  onClose: () => void;
  onSave: (word: Word) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Word | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Two-step save: first click on "Save" arms the confirm prompt; the second
  // click actually persists. Stops accidental taps on a tiny rounded button
  // from quietly adding things to the library.
  const [confirming, setConfirming] = useState(false);

  // Focus the input when the dialog mounts. Equivalent UX to autoFocus, but
  // satisfies jsx-a11y/no-autofocus by being explicit and ref-driven.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = input.trim();
  const alreadySaved = result ? existing.some((l) => l.hz === result.hz) : false;

  async function handleLookup() {
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setConfirming(false);
    try {
      const word = await getAIClient().lookup({ raw: trimmed });
      setResult(word);
    } catch (err) {
      setError((err as Error).message ?? 'Lookup failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div role="dialog" aria-modal aria-label="Add word" className="fixed inset-0 z-50 flex flex-col">
      <div
        className="absolute inset-0 anim-backdrop-in"
        style={{ background: 'rgba(26,22,20,0.45)' }}
        aria-hidden
        onClick={onClose}
      />
      <div
        className="relative mt-auto rounded-t-[28px] overflow-hidden anim-sheet-up"
        style={{ background: C.cream, boxShadow: '0 -10px 30px rgba(26,22,20,0.22)' }}
      >
        <span
          aria-hidden
          className="absolute top-2 left-1/2 w-10 h-1 rounded-full"
          style={{ transform: 'translateX(-50%)', background: 'rgba(26,22,20,0.18)' }}
        />

        <header
          className="pt-6 pb-3 px-5 flex items-center justify-between"
          style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
        >
          <div className="ff-display italic text-2xl flex items-baseline gap-2" style={{ color: C.ink }}>
            Add a word
            <span className="ff-zh-serif not-italic text-base opacity-50">· 添加生词</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: 'white' }}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="px-5 py-4 space-y-3 safe-bottom">
          <p className="ff-body text-[12px]" style={{ color: C.inkLight }}>
            Type or paste any Chinese word — pinyin and meaning will fill in.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleLookup();
            }}
            className="flex flex-col gap-2"
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setResult(null);
                setError(null);
              }}
              placeholder="例如 咖啡"
              className="w-full ff-zh-serif text-[18px] px-4 py-3 rounded-2xl outline-none transition-colors focus:border-vermillion"
              style={{ background: C.cream, border: `1px solid ${C.mist}`, color: C.ink }}
              aria-label="Chinese word to look up"
            />
            <button
              type="submit"
              disabled={!trimmed || loading}
              className="w-full px-4 py-3 rounded-2xl ff-body text-[12px] font-bold uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
              style={{ background: C.ink, color: 'white' }}
            >
              {loading ? <Loader2 size={12} className="animate-spin" aria-hidden /> : null}
              Look up
              <span className="ff-zh-serif tracking-normal opacity-80 ml-1">查询</span>
            </button>
          </form>

          {error && (
            <div
              className="p-3 rounded-2xl ff-body text-[12px]"
              style={{ background: C.blush, color: C.vermillionDeep }}
              role="alert"
            >
              {error}
            </div>
          )}

          {result && (
            <div
              className="p-3 rounded-2xl flex items-center gap-3 anim-slide-up shadow-paper"
              style={{ background: C.cream, border: `1px solid ${C.mist}` }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="ff-zh-serif text-2xl font-bold" style={{ color: C.ink }}>
                    {result.hz}
                  </span>
                  <span className="ff-body text-xs opacity-70" style={{ color: C.ink }}>
                    {result.py}
                  </span>
                </div>
                <div className="ff-body text-[12px]" style={{ color: C.inkLight }}>
                  {result.en}
                </div>
              </div>
              {alreadySaved ? (
                <span
                  className="ff-body text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full inline-flex items-center gap-1"
                  style={{ background: C.blush, color: C.vermillionDeep }}
                >
                  Saved
                  <span className="ff-zh-serif tracking-normal opacity-80">已保存</span>
                </span>
              ) : confirming ? (
                <div className="flex items-center gap-1.5 anim-scale-in">
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="ff-body text-[11px] font-semibold uppercase tracking-[0.15em] px-3 py-2 rounded-full inline-flex items-center gap-1"
                    style={{ background: 'white', color: C.inkLight, border: `1px solid ${C.blush}` }}
                  >
                    Cancel
                    <span className="ff-zh-serif tracking-normal opacity-80">取消</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onSave(result)}
                    className="ff-body text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-2 rounded-full inline-flex items-center gap-1"
                    style={{ background: C.vermillion, color: 'white' }}
                  >
                    <Check size={11} aria-hidden /> Confirm
                    <span className="ff-zh-serif tracking-normal opacity-80">确认</span>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  className="ff-body text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-2 rounded-full inline-flex items-center gap-1"
                  style={{ background: C.ink, color: 'white' }}
                >
                  <Plus size={11} aria-hidden /> Save
                  <span className="ff-zh-serif tracking-normal opacity-80">保存</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
